// components/BalanceSheet.jsx
import React, { useState, useEffect } from 'react';
import generalLedgerAPI from '../api/generalLedgerAPI';
import { useAuth } from '../context/AuthContext';
import ReportToolbar from '../shared/ReportToolbar';
import { computePeriodRange } from '../shared/PeriodSelect';

const BalanceSheet = () => {
  const { selectedClient } = useAuth();
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const accountingClientId = selectedClient?._id || localStorage.getItem('accountingClientId') || null;
  const accountingClientName = selectedClient?.name || localStorage.getItem('accountingClientName') || '';
  const [fiscalYear, setFiscalYear] = useState('');
  // Balance Sheet is a point-in-time report — the period preset's end date is
  // used as the "as of" date.
  const [period, setPeriod] = useState(() => ({ preset: 'YTD', ...computePeriodRange('YTD') }));
  const asOfDate = period.endDate;

  useEffect(() => {
    if (accountingClientId) {
      const fy = localStorage.getItem(`activeFiscalYear:${accountingClientId}`);
      if (fy) setFiscalYear(fy);
    }
  }, [accountingClientId]);

  // Listen for fiscal year changes from the header switcher
  useEffect(() => {
    const onFYChange = () => {
      if (accountingClientId) {
        const fy = localStorage.getItem(`activeFiscalYear:${accountingClientId}`);
        if (fy) setFiscalYear(fy);
      }
    };
    window.addEventListener('fiscalYear:changed', onFYChange);
    return () => window.removeEventListener('fiscalYear:changed', onFYChange);
  }, [accountingClientId]);

  const fetchBalanceSheet = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await generalLedgerAPI.getBalanceSheet(asOfDate, accountingClientId, fiscalYear || null);
      if (response.success) {
        setBalanceSheet(response.data);
      } else {
        setError(response.message || 'Failed to fetch balance sheet');
      }
    } catch (err) {
      setError(err.message || 'Error fetching balance sheet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accountingClientId) {
      fetchBalanceSheet();
    }
  }, [accountingClientId, asOfDate, fiscalYear]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);

  const formatCurrencyCSV = (amount) =>
    parseFloat((amount || 0).toFixed(2));

  // ── CSV Export ──────────────────────────────────────────────────────────────
  const exportToCSV = () => {
    if (!balanceSheet) return;

    const rows = [
      ['Section', 'Account Number', 'Account Name', 'Balance'],
      // Assets
      ...(balanceSheet.assets?.accounts || []).map(a => [
        'ASSETS', a.accountNumber, `"${a.accountName}"`, formatCurrencyCSV(a.currentBalance)
      ]),
      ['ASSETS', '', 'TOTAL ASSETS', formatCurrencyCSV(balanceSheet.assets?.total)],
      [],
      // Liabilities
      ...(balanceSheet.liabilities?.accounts || []).map(a => [
        'LIABILITIES', a.accountNumber, `"${a.accountName}"`, formatCurrencyCSV(a.currentBalance)
      ]),
      ['LIABILITIES', '', 'TOTAL LIABILITIES', formatCurrencyCSV(balanceSheet.liabilities?.total)],
      [],
      // Equity
      ...(balanceSheet.equity?.accounts || []).map(a => [
        'EQUITY', a.accountNumber, `"${a.accountName}"`, formatCurrencyCSV(a.currentBalance)
      ]),
      ['EQUITY', '', 'TOTAL EQUITY', formatCurrencyCSV(balanceSheet.equity?.total)],
      [],
      ['', '', 'TOTAL LIABILITIES & EQUITY',
        formatCurrencyCSV((balanceSheet.liabilities?.total || 0) + (balanceSheet.equity?.total || 0))],
      ['', '', 'Difference (should be 0)', formatCurrencyCSV(balanceSheet.totals?.difference)],
    ];

    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `balance-sheet-${asOfDate}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Print ───────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!balanceSheet) return;

    const fmt = (amount) =>
      new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);

    const accountRows = (accounts = []) =>
      accounts.map(a => `
        <tr>
          <td style="padding:4px 8px;color:#6b7280;">${a.accountNumber || ''}</td>
          <td style="padding:4px 8px;">${a.accountName || ''}</td>
          <td style="padding:4px 8px;text-align:right;font-family:monospace;">${fmt(a.currentBalance)}</td>
        </tr>`).join('');

    const totalRow = (label, amount, doubleBorder = false) => {
      const border = doubleBorder
        ? 'border-top:3px double #111;border-bottom:3px double #111;'
        : 'border-top:1px solid #9ca3af;';
      return `
        <tr>
          <td colspan="2" style="padding:6px 8px;font-weight:bold;${border}">${label}</td>
          <td style="padding:6px 8px;text-align:right;font-family:monospace;font-weight:bold;${border}">${fmt(amount)}</td>
        </tr>`;
    };

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Balance Sheet</title>
  <style>
    body { font-family: 'Courier New', Courier, monospace; font-size: 13px; color: #111; margin: 40px; }
    h1 { font-size: 20px; margin: 0 0 4px; }
    h2 { font-size: 15px; margin: 0 0 4px; font-weight: bold; }
    .header { text-align: center; margin-bottom: 32px; }
    .subheader { font-size: 13px; color: #4b5563; margin-top: 4px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
    .section-label { background: #f9fafb; }
    .toolbar { text-align: center; margin-bottom: 24px; }
    .toolbar button {
      padding: 8px 20px; font-size: 13px; cursor: pointer;
      background: #0d9488; color: #fff; border: none; border-radius: 4px;
      font-family: 'Courier New', Courier, monospace;
    }
    .toolbar button:hover { background: #0f766e; }
    @media print {
      .toolbar { display: none; }
      @page { margin: 20mm; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <button onclick="window.print()">🖨 Print / Save as PDF</button>
  </div>

  <div class="header">
    ${accountingClientName ? `<div style="font-size:18px;font-weight:bold;">${accountingClientName}</div>` : ''}
    <h1>Balance Sheet</h1>
    <div class="subheader">
      As of date: ${asOfDate}${fiscalYear ? `&nbsp;&nbsp;|&nbsp;&nbsp;Fiscal Year: ${fiscalYear}` : ''}
    </div>
  </div>

  <h2>ASSETS</h2>
  <table>
    ${accountRows(balanceSheet.assets?.accounts)}
    ${totalRow('TOTAL ASSETS', balanceSheet.assets?.total, true)}
  </table>

  <h2>LIABILITIES</h2>
  <table>
    ${accountRows(balanceSheet.liabilities?.accounts)}
    ${totalRow('TOTAL LIABILITIES', balanceSheet.liabilities?.total)}
  </table>

  <h2>EQUITY</h2>
  <table>
    ${accountRows(balanceSheet.equity?.accounts)}
    ${totalRow('TOTAL EQUITY', balanceSheet.equity?.total)}
  </table>

  <table>
    ${totalRow('TOTAL LIABILITIES & EQUITY',
      (balanceSheet.liabilities?.total || 0) + (balanceSheet.equity?.total || 0), true)}
  </table>

  ${isOutOfBalance
    ? `<div style="margin-top:16px;padding:10px;background:#fefce8;border:1px solid #fde68a;border-radius:4px;font-size:12px;color:#92400e;">
        <strong>Out of Balance:</strong> ${fmt(balanceSheet.totals?.difference)}
       </div>`
    : `<div style="margin-top:16px;padding:10px;background:#f0fdf4;border:1px solid #6ee7b7;border-radius:4px;font-size:12px;color:#065f46;text-align:center;font-weight:bold;">
        ✓ BALANCED
       </div>`}
</body>
</html>`;

    const win = window.open('', '_blank', 'width=850,height=950');
    win.document.write(html);
    win.document.close();
    setTimeout(() => {
      win.focus();
      win.print();
    }, 500);
  };

  const renderAccountRow = (account) => (
    <div className="flex justify-between ml-4 py-1">
      <span className="text-gray-900">
        {account.accountNumber && (
          <span className="mr-2 text-gray-500">{account.accountNumber}</span>
        )}
        {account.accountName}
      </span>
      <span className="text-gray-900 font-mono">{formatCurrency(account.currentBalance)}</span>
    </div>
  );

  const isOutOfBalance = balanceSheet && Math.abs(balanceSheet.totals?.difference || 0) > 0.01;

  return (
    <>
      <div className="w-full min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">

          {/* Controls — hidden on print */}
          <div className="no-print mb-8 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">BALANCE SHEET</h1>
            <div className="flex justify-center">
              <ReportToolbar
                period={period}
                onPeriodChange={setPeriod}
                fiscalYear={fiscalYear}
                onExportCSV={balanceSheet ? exportToCSV : undefined}
                onPrint={balanceSheet ? handlePrint : undefined}
                onRefresh={fetchBalanceSheet}
                refreshing={loading}
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="no-print mb-6 rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="no-print flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          )}

          {/* Printable content */}
          {!loading && balanceSheet && (
            <div className="bg-white p-12 font-mono text-sm max-w-3xl mx-auto">

              {/* Report Header */}
              <div className="text-center mb-8">
                {accountingClientName && (
                  <div className="text-xl font-bold text-gray-900 mb-1">{accountingClientName}</div>
                )}
                <div className="text-lg font-bold text-gray-900">Balance Sheet</div>
                <div className="text-sm text-gray-600 mt-1">
                  As of date: {asOfDate}
                  {fiscalYear && (
                    <span className="ml-4">| Fiscal Year: {fiscalYear}</span>
                  )}
                </div>
              </div>

              {/* ASSETS */}
              <div className="mb-8">
                <h2 className="text-base font-bold text-gray-900 mb-2">ASSETS</h2>
                <div className="space-y-0.5 mb-3">
                  {(balanceSheet.assets?.accounts || []).map((account, idx) => (
                    <div key={`asset-${account._id || account.accountNumber || idx}`}>
                      {renderAccountRow(account)}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between font-bold text-gray-900 border-t-2 border-b-2 border-gray-900 py-2">
                  <span>TOTAL ASSETS</span>
                  <span>{formatCurrency(balanceSheet.assets?.total || 0)}</span>
                </div>
              </div>

              <div className="h-6" />

              {/* LIABILITIES */}
              <div className="mb-6">
                <h2 className="text-base font-bold text-gray-900 mb-2">LIABILITIES</h2>
                <div className="space-y-0.5 mb-3">
                  {(balanceSheet.liabilities?.accounts || []).map((account, idx) => (
                    <div key={`liability-${account._id || account.accountNumber || idx}`}>
                      {renderAccountRow(account)}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between font-semibold text-gray-900 border-t border-gray-400 py-2">
                  <span>TOTAL LIABILITIES</span>
                  <span>{formatCurrency(balanceSheet.liabilities?.total || 0)}</span>
                </div>
              </div>

              {/* EQUITY */}
              <div className="mb-6">
                <h2 className="text-base font-bold text-gray-900 mb-2">EQUITY</h2>
                <div className="space-y-0.5 mb-3">
                  {(balanceSheet.equity?.accounts || []).map((account, idx) => (
                    <div key={`equity-${account._id || account.accountNumber || idx}`}>
                      {renderAccountRow(account)}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between font-semibold text-gray-900 border-t border-gray-400 py-2">
                  <span>TOTAL EQUITY</span>
                  <span>{formatCurrency(balanceSheet.equity?.total || 0)}</span>
                </div>
              </div>

              {/* Total Liabilities & Equity */}
              <div className="flex justify-between font-bold text-gray-900 text-base border-t-2 border-b-2 border-gray-900 py-3 mt-2">
                <span>TOTAL LIABILITIES &amp; EQUITY</span>
                <span>{formatCurrency((balanceSheet.liabilities?.total || 0) + (balanceSheet.equity?.total || 0))}</span>
              </div>

              {/* Out of Balance */}
              {isOutOfBalance && (
                <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
                  <strong>Out of Balance:</strong> {formatCurrency(balanceSheet.totals.difference)}
                </div>
              )}

              {/* Balanced badge */}
              {!isOutOfBalance && (
                <div className="mt-6 p-3 bg-teal-50 border border-teal-200 rounded-md text-sm text-teal-700 text-center font-semibold">
                  ✓ BALANCED
                </div>
              )}

            </div>
          )}

          {!loading && !balanceSheet && !error && (
            <div className="no-print text-center text-gray-500 py-12">
              Select a date and click Refresh to view the balance sheet
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BalanceSheet;
